import { cn } from '@heidi/utils';
import { useEffect, useRef, useState } from 'react';
import type {
  ComposerDictationMicAnalyzer,
  ComposerDictationPorts,
} from './composer-dictation-bar';

// Bar pitch (bar + gap) in px. The bar count is derived from the measured track
// width so the stream fills the composer at any viewport size.
const BAR_PITCH_PX = 6;
// The Work homepage composer is capped at `max-w-2xl` (672px), which leaves its
// waveform track at about 558px, or about 93 bars at BAR_PITCH_PX. The
// reference sits a little under that so the homepage row keeps the plain
// PUSH_INTERVAL_MS (and so scrolls exactly as before) down to a ~537px track.
// It also sets how long a narrower row's visible time window should be.
const REFERENCE_BARS = 90;
// Below NARROW_WIDTH_THRESHOLD_PX (e.g. the ~270-300px Ask Heidi side panel)
// bars are packed tighter, so a narrow row needs less interval stretch to hold
// the homepage's time window and keeps scrolling smoothly. The threshold sits
// well under the homepage track so the homepage keeps its 6px pitch.
const NARROW_BAR_PITCH_PX = 4;
const NARROW_WIDTH_THRESHOLD_PX = 400;
const MIN_BARS = 24;
const MAX_BARS = 200;
// Exported so the test suite can assert against the same resting height
// rather than duplicating this magic number.
export const MIN_PX = 3; // resting / silent bar height
const TRACK_PX = 28; // tallest a bar can grow within the size-9 control row
// How often a fresh sample is pushed onto the stream (ms). Lower = faster scroll.
const PUSH_INTERVAL_MS = 70;
// REFERENCE_BARS * PUSH_INTERVAL_MS is the homepage row's visible time window.
// Narrower rows have fewer bars, so the push interval is stretched (see
// pushIntervalForBarCount) to keep roughly that same window on screen instead
// of scrolling it off in a fraction of the time.
const REFERENCE_WINDOW_MS = REFERENCE_BARS * PUSH_INTERVAL_MS;
// Upper bound on the stretched interval so a very narrow row still feels live
// rather than choppy.
const MAX_PUSH_INTERVAL_MS = 160;
const EASE = 0.35; // smoothing applied to the incoming level before it's pushed

const pushIntervalForBarCount = (barCount: number): number => {
  if (barCount <= 0) return PUSH_INTERVAL_MS;
  return Math.min(
    MAX_PUSH_INTERVAL_MS,
    Math.max(PUSH_INTERVAL_MS, REFERENCE_WINDOW_MS / barCount)
  );
};

export interface ComposerDictationWaveformProps {
  /** While true the stream advances with live mic input; when false it freezes. */
  readonly listening: boolean;
  readonly ports: ComposerDictationPorts;
  readonly className?: string;
}

/**
 * Live, scrolling waveform for the composer's dictation row. Each tick samples
 * the current voice energy (the mic-selector `getBarHeights` mapping on web, or
 * the host's native audio levels on desktop), pushes it onto a ring buffer and
 * scrolls the buffer across width-fitted bars, so the wave streams in from the
 * trailing edge and its height tracks how loud the speech is at each moment.
 */
export const ComposerDictationWaveform = ({
  listening,
  ports,
  className,
}: ComposerDictationWaveformProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const barsRef = useRef<(HTMLSpanElement | null)[]>([]);
  const [barCount, setBarCount] = useState(64);

  const native = ports.shouldUseNativeAudio();
  const { getMicAnalyzer, releaseMicAnalyzer } = ports.useAudioAnalyzer();
  const readyToRequestMic = ports.useReadyToRequestMic();
  // Nothing publishes native levels unless a consumer holds the AUDIO_LEVELS
  // listener; hold it only while this row is live on a native-capture host.
  ports.useAudioLevelsSubscription(native && listening);

  // React nulls the refs of removed bars but never shortens the array, so trim
  // it after a shrink. The loop reads its length as the live bar count (for the
  // stream size and the push interval), and a stale length would keep pushing
  // the newest sample into a bar that is no longer rendered.
  useEffect(() => {
    barsRef.current.length = barCount;
  }, [barCount]);

  // Derive the bar count from the measured track width.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const measure = () => {
      const width = el.clientWidth;
      if (width > 0) {
        const pitch =
          width < NARROW_WIDTH_THRESHOLD_PX ? NARROW_BAR_PITCH_PX : BAR_PITCH_PX;
        setBarCount(Math.min(MAX_BARS, Math.max(MIN_BARS, Math.round(width / pitch))));
      }
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Stream live audio across the bars while listening; freeze when not.
  useEffect(() => {
    if (!listening) return;

    let raf = 0;
    let alive = true;
    // The analyser the host's audio graph hands out, reduced to the members
    // sampled here — not the DOM AnalyserNode.
    let mic: ComposerDictationMicAnalyzer | null = null;
    let freq = new Uint8Array();
    let level = MIN_PX; // eased current voice energy
    let lastPush = 0;
    // Ring buffer of past samples: index 0 is oldest (start), last is newest (end).
    let stream: number[] = [];

    const ensureStream = (size: number) => {
      if (stream.length === size) return;
      const next = new Array<number>(size).fill(MIN_PX);
      // Keep the most recent samples against the newest edge.
      const keep = Math.min(stream.length, size);
      for (let i = 0; i < keep; i++) {
        next[size - 1 - i] = stream[stream.length - 1 - i] ?? MIN_PX;
      }
      stream = next;
    };

    const sampleLevel = (): number => {
      if (native) {
        // Imperative read: the host's native levels tick at ~30 FPS and this
        // component only needs their value once per push, not a re-render per
        // frame. Calling the port here, inside the loop, is what keeps this
        // fresh every frame instead of a one-time snapshot.
        const levels = ports.readNativeAudioLevels();
        return levels?.length ? Math.max(...levels) : MIN_PX;
      }
      if (!mic) return MIN_PX;
      mic.analyzer.getByteFrequencyData(freq);
      return Math.max(...ports.getBarHeights(freq));
    };

    const render = () => {
      const bars = barsRef.current;
      for (let i = 0; i < bars.length; i++) {
        const bar = bars[i];
        if (!bar) continue;
        bar.style.height = `${Math.max(MIN_PX, Math.min(TRACK_PX, stream[i] ?? MIN_PX))}px`;
      }
    };

    const tick = (ts: number) => {
      if (!alive) return;
      level += (sampleLevel() - level) * EASE;
      const barCount = barsRef.current.length;
      if (ts - lastPush >= pushIntervalForBarCount(barCount)) {
        lastPush = ts;
        ensureStream(barCount);
        stream.shift();
        stream.push(level);
        render();
      }
      raf = requestAnimationFrame(tick);
    };

    const startWeb = async () => {
      try {
        const acquired = await getMicAnalyzer();
        // Teardown (unmount / listening off) can win the race against this async
        // acquisition. Release at once so the mic analyser never leaks: the
        // cleanup below already ran with `mic` still null.
        if (!alive) {
          void releaseMicAnalyzer(acquired.id);
          return;
        }
        mic = acquired;
        // Same analyser config as the mic-selector bars.
        mic.analyzer.fftSize = 512;
        mic.analyzer.minDecibels = -115;
        mic.analyzer.maxDecibels = -15;
        mic.analyzer.smoothingTimeConstant = 0.4;
        freq = new Uint8Array(mic.analyzer.frequencyBinCount);
        raf = requestAnimationFrame(tick);
      } catch {
        // Mic unavailable: the bars stay at rest.
      }
    };

    if (native) raf = requestAnimationFrame(tick);
    else if (readyToRequestMic) void startWeb();

    return () => {
      alive = false;
      cancelAnimationFrame(raf);
      if (mic) void releaseMicAnalyzer(mic.id);
    };
  }, [listening, native, readyToRequestMic, getMicAnalyzer, releaseMicAnalyzer, ports]);

  return (
    <div
      ref={containerRef}
      aria-hidden
      data-testid="composer-dictation-waveform"
      className={cn('flex h-9 items-center justify-between overflow-hidden', className)}
    >
      {Array.from({ length: barCount }, (_, i) => (
        <span
          key={i}
          ref={(el) => {
            barsRef.current[i] = el;
          }}
          className="w-0.5 shrink-0 rounded-full bg-text-tertiary transition-[height] duration-100 ease-linear"
          style={{ height: `${MIN_PX}px` }}
        />
      ))}
    </div>
  );
};
